import type { AssetProcessingActivityDetail } from 'config/api/assetProcessingActivities/assetProcessingActivities.types';
import type { AvailableProcessingActivity, OwnerWorkspaceAsset } from 'config/api/assetRopa/assetRopaWorkspace.types';
import type { DpiaRequest, DpiaRequestContext, DpiaRequestOpenResponse } from 'config/api/dpiaRequests/dpiaRequests.types';
import type { DpiaContext } from 'config/api/dpias/dpias.types';
import type { AssetProcessingActivityReviewRow } from 'config/api/ropaReviews/ropaReviews.types';

const pickString = (...values: Array<string | null | undefined>): string => {
  const match = values.find(value => typeof value === 'string' && value.trim());
  return typeof match === 'string' ? match.trim() : '';
};

const pickStringList = (...lists: string[][]): string[] => lists.find(list => list.length > 0) ?? [];

const mapCategoryNames = (items: Array<{ name: string }>): string[] =>
  items.map(item => item.name).filter((name): name is string => Boolean(name.trim()));

const RECOMMENDATION_LABELS = {
  normally_not_recommended: 'DPIA is normally not recommended',
  further_assessment_recommended: 'Further assessment is recommended',
  dpia_recommended: 'DPIA is recommended',
  dpia_strongly_recommended: 'DPIA is strongly recommended',
} as const;

const formatRecommendation = (recommendation: DpiaRequest['recommendation']): string =>
  RECOMMENDATION_LABELS[recommendation] ?? recommendation;

const formatDecision = (request: DpiaRequest): string => {
  if (request.decision === 'dpia_required' || request.status === 'decided_dpia_required') {
    return 'DPIA shall be conducted';
  }

  if (request.decision === 'dpia_not_required' || request.status === 'decided_dpia_not_required') {
    return 'DPIA is not required';
  }

  return '';
};

const findProcessingActivityRow = (
  asset: OwnerWorkspaceAsset | null | undefined,
  assetProcessingActivityId: number,
): AssetProcessingActivityReviewRow | undefined =>
  asset?.processingActivityReviews.find(row => row.assetProcessingActivityId === assetProcessingActivityId);

export const findWorkspaceAssetByApaId = (
  assets: OwnerWorkspaceAsset[],
  assetProcessingActivityId: number,
): OwnerWorkspaceAsset | undefined =>
  assets.find(asset => asset.processingActivityReviews.some(row => row.assetProcessingActivityId === assetProcessingActivityId));

export const findCatalogProcessingActivity = (
  activities: AvailableProcessingActivity[],
  processingActivityId: number,
): AvailableProcessingActivity | undefined => activities.find(activity => activity.id === processingActivityId);

export const mergeDpiaEditorContext = ({
  apaDetail,
  assetProcessingActivityId = 0,
  assetRopaAsset,
  base,
  catalogActivity,
  sourceRequest,
}: {
  base: DpiaContext;
  sourceRequest?: DpiaRequestOpenResponse | null;
  apaDetail?: AssetProcessingActivityDetail | null;
  assetProcessingActivityId?: number;
  assetRopaAsset?: OwnerWorkspaceAsset | null;
  catalogActivity?: AvailableProcessingActivity | null;
}): DpiaContext => {
  const requestContext: DpiaRequestContext | undefined = sourceRequest?.context;
  const request = sourceRequest?.request;
  const apaCountries = apaDetail?.countries.map(country => country.countryCode).filter(Boolean) ?? [];
  const apaId = assetProcessingActivityId || apaDetail?.id || 0;
  const processingActivityRow = findProcessingActivityRow(assetRopaAsset, apaId);
  const operationalOwnerName = pickString(
    base.operationalOwnerName ?? '',
    requestContext?.assetOwnerName,
    assetRopaAsset?.operationalOwnerName,
  );

  return {
    ...base,
    assetName: pickString(base.assetName, requestContext?.assetName, assetRopaAsset?.assetName),
    assetTypeName: pickString(base.assetTypeName, assetRopaAsset?.assetTypeName),
    assetProcessingActivityName: pickString(
      base.assetProcessingActivityName,
      requestContext?.processingActivityName,
      apaDetail?.processingActivity.name,
      processingActivityRow?.processingActivityName,
    ),
    operationalOwnerName: operationalOwnerName || null,
    dataSubjectCategories: pickStringList(
      base.dataSubjectCategories,
      requestContext ? mapCategoryNames(requestContext.dataSubjectCategories) : [],
      catalogActivity?.dataSubjectCategories ?? [],
    ),
    personalDataCategories: pickStringList(
      base.personalDataCategories,
      requestContext ? mapCategoryNames(requestContext.personalDataCategories) : [],
      catalogActivity?.personalDataCategories ?? [],
    ),
    countryHandling: pickStringList(
      base.countryHandling,
      requestContext?.countrySummary.countryCodes ?? [],
      apaCountries,
      processingActivityRow?.countriesSummary ? processingActivityRow.countriesSummary.split(',').map(code => code.trim()) : [],
    ),
    minimumSecurityAssessment: pickString(
      requestContext?.minimumSecurityAssessment,
      base.minimumSecurityAssessment,
      apaDetail?.processingActivity.minimumSecurityMeasures,
      apaDetail?.minimumSecurityAssessment,
    ),
    availablePurposes: base.availablePurposes,
    sourceRequest: {
      recommendation: pickString(base.sourceRequest.recommendation, request ? formatRecommendation(request.recommendation) : ''),
      decision: pickString(base.sourceRequest.decision, request ? formatDecision(request) : ''),
      decisionComment: pickString(base.sourceRequest.decisionComment, request?.decisionComment),
      triggerSource: base.sourceRequest.triggerSource ?? request?.triggerSource ?? null,
      triggerComment: pickString(base.sourceRequest.triggerComment, request?.triggerComment),
    },
  };
};
